import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import API from '@/config/api';
import SEO from '@/components/SEO';
import {
  BookOpen, CheckCircle, Crown, Star, ArrowRight,
  Smartphone, BookMarked, Award, Sparkles, ShieldCheck
} from 'lucide-react';

const BOOK_COVER = "https://customer-assets.emergentagent.com/job_3a854a85-d099-4060-868a-517959e97093/artifacts/c7l5gfu4_7289C4F8-554B-401A-9251-05B028DA18D5.png";

const editions = [
  {
    id: "wealth_lexicon_ebook",
    label: "eBook",
    price: 39,
    icon: Smartphone,
    color: "blue",
    popular: false,
    includes: ["Digital eBook (PDF + ePub)", "Instant delivery", "Read on any device"],
  },
  {
    id: "wealth_lexicon_softback",
    label: "Softback",
    price: 75,
    icon: BookOpen,
    color: "emerald",
    popular: false,
    includes: ["Softback print edition", "Digital eBook included", "Ships to your door"],
  },
  {
    id: "wealth_lexicon_hardback",
    label: "Hardback",
    price: 150,
    icon: BookMarked,
    color: "gold",
    popular: true,
    includes: ["Premium hardback edition", "Digital eBook included", "Collector's quality"],
  },
  {
    id: "wealth_lexicon_vip",
    label: "VIP Bundle",
    price: 250,
    icon: Crown,
    color: "purple",
    popular: false,
    includes: ["Hardback print edition", "The Wealth Lexicon Workbook", "Digital eBook included", "Complete learning system"],
  },
];

export default function BookPreorder() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handlePreorder = async (editionId) => {
    if (!isAuthenticated) {
      sessionStorage.setItem('pendingPreorder', editionId);
      navigate('/login');
      return;
    }

    setLoading(editionId);
    try {
      const response = await fetch(`${API}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: editionId,
          package_type: 'preorder',
          origin_url: window.location.origin,
        }),
      });
      const data = await response.json();
      if (data.success && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.detail || 'Failed to start checkout');
      }
    } catch (err) {
      console.error('Preorder checkout error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const accentOf = (color) => {
    if (color === 'gold') return { bg: 'bg-[#D4AF37]/20', text: 'text-[#D4AF37]', border: 'border-[#D4AF37]/40', ring: 'ring-[#D4AF37]/20', btn: 'bg-[#D4AF37] hover:bg-[#B8962E] text-black' };
    if (color === 'purple') return { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/40', ring: 'ring-purple-500/20', btn: 'bg-purple-600 hover:bg-purple-700 text-white' };
    if (color === 'emerald') return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/40', ring: 'ring-emerald-500/20', btn: 'bg-emerald-600 hover:bg-emerald-700 text-white' };
    return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/40', ring: 'ring-blue-500/20', btn: 'bg-blue-600 hover:bg-blue-700 text-white' };
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="book-preorder-page">
      <SEO title="The Wealth Lexicon - Pre-Order | Nouveau Riche Legacy" description="Pre-order The Wealth Lexicon - your essential guide to financial literacy and building generational wealth." />

      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#14120f] to-[#0a0a0f]" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] border border-[#D4AF37]/10 rounded-full" />
          <div className="absolute bottom-10 right-1/4 w-80 h-80 border border-[#D4AF37]/5 rounded-full" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left – Text */}
            <div>
              <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-6">
                <Sparkles className="h-3 w-3 mr-1" /> Pre-Order Now
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                The Wealth <span className="text-[#D4AF37]">Lexicon</span>
              </h1>
              <p className="text-lg text-[#e9dcc9] mb-2 font-display italic max-w-lg">
                Don't Let Them Speak Circles Around You
              </p>
              <p className="text-gray-400 mb-8 max-w-lg">
                Your essential guide to financial literacy and building generational wealth. From credit building to investing strategies, The Wealth Lexicon gives you the vocabulary and knowledge to command your financial future.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <ShieldCheck className="h-5 w-5 text-[#D4AF37]" /> Secure Checkout
                </div>
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Star className="h-5 w-5 text-[#D4AF37]" /> Limited Release
                </div>
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Award className="h-5 w-5 text-[#D4AF37]" /> By NRL
                </div>
              </div>

              <Button
                onClick={() => document.getElementById('editions').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-black text-lg px-10 py-6"
                data-testid="preorder-hero-cta"
              >
                Choose Your Edition <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Right – Book */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-8 bg-[#D4AF37]/10 blur-3xl rounded-full" />
                <img
                  src={BOOK_COVER}
                  alt="The Wealth Lexicon"
                  className="relative z-10 w-72 sm:w-80 rounded-lg shadow-2xl shadow-[#D4AF37]/20"
                  data-testid="book-cover-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editions Grid */}
      <section id="editions" className="py-24 px-4 bg-[#14120f]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Choose Your <span className="text-[#D4AF37]">Edition</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Four ways to own The Wealth Lexicon. Pick the format that fits your legacy.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {editions.map((ed) => {
              const a = accentOf(ed.color);
              return (
                <Card
                  key={ed.id}
                  className={`bg-[#1c1814] ${ed.popular ? `border-[#D4AF37] ring-2 ${a.ring}` : 'border-[#2d2722]'} relative flex flex-col`}
                  data-testid={`edition-card-${ed.id}`}
                >
                  {ed.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-[#D4AF37] text-black">
                        <Crown className="h-3 w-3 mr-1" /> Most Popular
                      </Badge>
                    </div>
                  )}
                  <CardContent className="pt-8 pb-6 flex flex-col flex-1">
                    <div className={`w-14 h-14 ${a.bg} rounded-xl flex items-center justify-center mb-4`}>
                      <ed.icon className={`h-7 w-7 ${a.text}`} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">{ed.label}</h3>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-[#D4AF37]">${ed.price}</span>
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {ed.includes.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                          <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${a.text}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => handlePreorder(ed.id)}
                      disabled={loading === ed.id}
                      className={`w-full ${ed.popular ? 'bg-[#D4AF37] hover:bg-[#B8962E] text-black' : a.btn}`}
                      data-testid={`preorder-btn-${ed.id}`}
                    >
                      {loading === ed.id ? 'Processing...' : 'Pre-Order Now'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-24 px-4 bg-[#0a0a0f]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What You'll <span className="text-[#D4AF37]">Learn</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Financial Literacy Vocabulary", desc: "Gain command of the essential terms and concepts that drive personal finance, banking, and wealth management." },
              { title: "Market Vocabulary & Understanding", desc: "Decode the language of stocks, crypto, forex, and commodities so you can navigate any market with confidence." },
              { title: "Peace of Mind", desc: "Replace financial anxiety with clarity. Know exactly what's being said, what it means, and how it affects your money." },
              { title: "Access to Larger Money Conversations", desc: "Speak fluently in boardrooms, investment meetings, and high-net-worth circles — and be taken seriously." },
            ].map((topic, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#14120f] border border-[#1c1814] hover:border-[#D4AF37]/30 transition-colors">
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">{topic.title}</h3>
                  <p className="text-gray-400 text-sm">{topic.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#14120f] to-[#0a0a0f]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Secure Your Copy <span className="text-[#D4AF37]">Today</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            The Wealth Lexicon is a limited first-edition release. Pre-order now to guarantee yours.
          </p>
          <Button
            onClick={() => document.getElementById('editions').scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#D4AF37] hover:bg-[#B8962E] text-black text-lg px-12 py-6"
            data-testid="preorder-bottom-cta"
          >
            Pre-Order The Wealth Lexicon <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
